import { Languages } from '@ratehub/base-ui';

import getCardFinderURLSegments, { CARDFINDER_URL_SEGMENT_SLUGS } from './getCardFinderURLSegments';


/**
 * Constructs a localized URL path that always starts at the credit card home, and optionally appends
 * an additional path or query string if provided.
 * @param {string} language - accepts either languageCode or locale
 * @param {string} [ccSlug=CC_URL_SLUGS.BASE] - a recognized key from within CC_URL_SLUGS
 * @param {string} [additionalPath=''] - optional additional path
 * @returns {string} URL path from root that includes the localized top-level credit card slug
 */
function getCreditCardURL(
    language,
    ccSlug = CC_URL_SLUGS.BASE,
    additionalPath = '',
) {
    const urlLanguage = language?.slice(0, 2);

    if (![ Languages.ENGLISH, Languages.FRENCH ].includes(urlLanguage)) {
        throw new RangeError(`getCreditCardURL: unrecognized language code “${urlLanguage}”`);
    }

    const pathForSlug = CC_URLS[ccSlug]?.[urlLanguage];
    if (!pathForSlug) {
        throw new RangeError(`getCreditCardURL: unrecognized CC slug “${ccSlug}” • ${typeof ccSlug}`);
    }

    const addedPath = (
        additionalPath && additionalPath.slice(0, 1) !== '/'
            ? '/'
            : ''
    ) + additionalPath;

    // this ensures all URLs returned have the BASE URL as the starting point
    return (
        ccSlug === CC_URL_SLUGS.BASE
            ? ''
            : CC_URLS[CC_URL_SLUGS.BASE][urlLanguage]
    )
        + pathForSlug
        + addedPath;
}


// - getCreditCardURL() will _always_ return a string that starts from the BASE (/credit-cards) and returns
//   a full URL, but the entries in this object need not repeat it for every property
// - using getCardFinderURLSegments() means individual segment strings never appear twice in code
const CC_URLS = {
    BASE: {
        en: '/credit-cards',
        fr: '/cartes-de-credit',
    },
    REWARDS_CALCULATOR: {
        en: '/rewards-calculator',
        fr: '/calculatrice-de-recompenses',
    },
    SCENARIO_CHOOSER: {
        en: '/scenario-chooser',
        fr: '/selecteur-de-scenario',
    },
    CARDFINDER: {
        en:  getCardFinderURLSegments('en', CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT),
        fr:  getCardFinderURLSegments('fr', CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT),
    },
    CARDFINDER_RESUME: {
        en:  getCardFinderURLSegments('en', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.RESUME_SEGMENT ]
        ),
        fr:  getCardFinderURLSegments('fr', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.RESUME_SEGMENT ]
        ),
    },
    CARDFINDER_CARD_TYPE: {
        en:  getCardFinderURLSegments('en', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.CARD_TYPE_SEGMENT ]
        ),
        fr:  getCardFinderURLSegments('fr', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.CARD_TYPE_SEGMENT ]
        ),
    },
    CARDFINDER_ELIGIBILITY_CHECKER: {
        en:  getCardFinderURLSegments('en', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.ELIGIBILITY_CHECKER_SEGMENT ]
        ),
        fr:  getCardFinderURLSegments('fr', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.ELIGIBILITY_CHECKER_SEGMENT ]
        ),
    },
    CARDFINDER_RESULTS: {
        en:  getCardFinderURLSegments('en', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.RESULTS_SEGMENT ]
        ),
        fr:  getCardFinderURLSegments('fr', [
            CARDFINDER_URL_SEGMENT_SLUGS.CARDFINDER_SEGMENT,
            CARDFINDER_URL_SEGMENT_SLUGS.RESULTS_SEGMENT ]
        ),
    },
    APPLY: {
        en: '/apply',
        fr: '/demande',
    },
    DETAILS: {
        en: '/card',
        fr: '/carte',
    },
    CARD_COMPARE: {
        en: '/card-compare',
        fr: '/comparaison-cartes',
    },
};

// you may use this map to ensure the slug argument to getCreditCardURL() is without typos
//  CC_URL_SLUGS = {
//      'BASE': 'BASE',
//      'REWARDS_CALCULATOR': 'REWARDS_CALCULATOR',
//      ...
const CC_URL_SLUGS = Object.keys(CC_URLS).reduce((slugs, thisSlug) => ({
    ...slugs,
    [thisSlug]: thisSlug,
}), {});


export { CC_URL_SLUGS };
export default getCreditCardURL;
